import {
  Card,
  Form,
  Input,
  Flex,
  Row,
  Col,
  Typography,
  Switch,
  Checkbox,
} from "antd";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import PasswordRequirements from "./PasswordRequirements";

const { Text } = Typography;

const SignInSettings = ({ form }) => {
  const themeDefault = useSelector((state) => state.themeDefault);
  const borderStyle = themeDefault?.info?.newConfig?.cardborder;
  // SMALL TITLE + SMALL SUBTITLE
  const TitleText = ({ children, size = 13 }) => (
    <Text style={{ fontWeight: 700, fontSize: size }}>{children}</Text>
  );

  const SubtitleText = ({ children, size = 12 }) => (
    <Text style={{ fontWeight: 400, fontSize: size, color: "#A8A8A8" }}>
      {children}
    </Text>
  );

  // Watch values
  const auto = Form.useWatch("autoGeneratePassword", form);
  const pwdLogin = Form.useWatch("passwordEnabled", form);
  const otpEmail = Form.useWatch("otpEmailEnabled", form);
  const otpPhone = Form.useWatch("otpPhoneEnabled", form);
const otpEnabled = Form.useWatch("otpEnabled", form);

  // Auto password logic
  useEffect(() => {
    if (auto) {
      form.setFieldsValue({
        password: "",
        confirmPassword: "",
      });
    } else {
      const currentPwd = form.getFieldValue("password");
      const currentConfirm = form.getFieldValue("confirmPassword");
      if (currentPwd || currentConfirm) {
        form.setFieldsValue({
          password: "",
          confirmPassword: "",
        });
      }
    }
  }, [auto, form]);

useEffect(() => {
  if (auto) {
    // Auto-check send email checkbox
    form.setFieldsValue({
      sendPasswordInEmail: true,
    });
  }
}, [auto, form]);
  // Reusable UI component
  const SettingItem = ({ title, subtitle, name, switchSize = "default" }) => (
    <Flex gap={8} vertical align="flex-start" justify="flex-start">
      <TitleText>{title}</TitleText>
      <SubtitleText>{subtitle}</SubtitleText>
      <Form.Item name={name} valuePropName="checked" style={{ margin: 0 }}>
        <Switch size={switchSize} />
      </Form.Item>
    </Flex>
  );

  return (
    <Card
      bordered
      title={<span style={{ fontSize: 13 }}>Sign In Settings</span>}>
      {/* ---------------- OTP ROW ---------------- */}
      <Row
        gutter={[16, 16]}
        style={{ borderTop: borderStyle, paddingTop: 16, marginTop: -16 }}>
        <Col xs={24} sm={24} md={8} lg={6} xl={4}>
          <Flex
            gap={8}
            vertical
            align="flex-start"
            justify="center"
            style={{
              borderRight: borderStyle,
              borderBottom: borderStyle,
              paddingBottom: 16,
              minHeight: 150,
            }}>
            <SettingItem
              title="Allow OTP"
              subtitle="Login via OTP"
              name="otpEnabled"
            />
          </Flex>
        </Col>

        <Col xs={24} sm={24} md={16} lg={18} xl={18}>
          <Flex
            gap={16}
            vertical
            align="flex-start"
            style={{
              minHeight: 150,
              borderBottom: borderStyle,
              padding: 8,
            }}>
            <SettingItem
              title="OTP via Email"
              subtitle=""
              name="otpEmailEnabled"
              switchSize="small"
            />
            <SettingItem
              title="OTP via Phone"
              subtitle=""
              name="otpPhoneEnabled"
              switchSize="small"
            />

            {otpEnabled && otpEmail === false && otpPhone === false && (
              <span style={{ color: "red", fontSize: 11 }}>
                At least one OTP option required.
              </span>
            )}
          </Flex>
        </Col>
      </Row>

      {/* ---------------- PASSWORD LOGIN ROW ---------------- */}
      <Row gutter={[16, 16]}>
        {/* LEFT PANE */}
        <Col xs={24} sm={24} md={8} lg={6} xl={4}>
          <Flex
            gap={8}
            vertical
            align="flex-start"
            justify="start"
            style={{
              borderRight: borderStyle,
              borderBottom: borderStyle,
              paddingBottom: 16,
              paddingTop: 16,
              minHeight: 312,
            }}>
            <SettingItem
              title="Login via password"
              subtitle=""
              name="passwordEnabled"
            />
          </Flex>
        </Col>

        {/* MIDDLE PANE */}
        <Col xs={24} sm={24} md={16} lg={9} xl={8}>
          <Flex
            gap={12}
            vertical
            align="flex-start"
            style={{
              minHeight: 265,
              borderBottom: borderStyle,
              paddingLeft: 5,
              paddingTop: 24,
            }}>
            <Form.Item
              name="autoGeneratePassword"
              valuePropName="checked"
              style={{ marginBottom: 4 }}>
              <Checkbox style={{ fontSize: 12, fontWeight: 700, margin: 0 }}>
                Automatically generate a secure password
              </Checkbox>
            </Form.Item>

            <Form.Item
              name="requirePasswordChange"
              valuePropName="checked"
              style={{ marginBottom: 4 }}>
              <Checkbox style={{ fontSize: 12, fontWeight: 700, margin: 0 }}>
                Require this user to change their password when first sign in
              </Checkbox>
            </Form.Item>

            <Form.Item
              name="sendPasswordInEmail"
              valuePropName="checked"
              style={{ marginBottom: 4 }}>
              <Checkbox style={{ fontSize: 12, fontWeight: 700, margin: 0 }}>
                Send password in email upon completion
              </Checkbox>
            </Form.Item>

            <SubtitleText size={12} style={{ marginBottom: 4 }}>
              Email recipients <span style={{ color: "red" }}>*</span>
            </SubtitleText>

            <Form.Item
              style={{ width: "100%" }}
              name="passwordEmailRecipients"
              rules={[
                ({ getFieldValue }) => ({
                  required: getFieldValue("sendPasswordInEmail"),
                  message: "Please enter email addresses",
                }),
              ]}>
              <Input
                placeholder="Enter email addresses"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Flex>
        </Col>

        {/* RIGHT PANE */}
        <Col xs={24} sm={24} md={24} lg={9} xl={12}>
          <Flex
            gap={8}
            vertical
            align="flex-start"
            style={{
              minHeight: 320,
              borderLeft: borderStyle,
              paddingLeft: 24,
              paddingTop: 24,
            }}>
            {
              <>
                <TitleText>Set Password</TitleText>
                <SubtitleText>Password must meet requirements.</SubtitleText>

                <Row
                  gutter={16}
                  style={{
                    borderBottom: borderStyle,
                    paddingBottom: 52,
                  }}>
                  <Col xs={24} sm={24} md={12}>
                    <Form.Item
                      name="password"
                      label="Password"
                      style={{ marginBottom: 8 }}
                      rules={[
                        { required: !auto, message: "Password required" }, // required only if auto is false
                      ]}>
                      <Input.Password
                        placeholder="Enter Password"
                        disabled={auto} // disabled if auto-generate is checked
                      />
                    </Form.Item>

                    <Form.Item
                      name="confirmPassword"
                      label="Confirm Password"
                      style={{ marginBottom: 0 }}
                      dependencies={["password"]}
                      rules={[
                        {
                          required: !auto, // required only if auto is false
                          message: "Confirm password required",
                        },
                        ({ getFieldValue }) => ({
                          validator(_, val) {
                            if (auto || getFieldValue("password") === val)
                              return Promise.resolve();
                            return Promise.reject(
                              new Error("Passwords do not match")
                            );
                          },
                        }),
                      ]}>
                      <Input.Password
                        placeholder="Enter Confirm Password"
                        disabled={auto} // disabled if auto-generate is checked
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={24} md={12}>
                    <PasswordRequirements />
                  </Col>
                </Row>
              </>
            }
          </Flex>
        </Col>
      </Row>
    </Card>
  );
};

export default SignInSettings;
